/* Share a character as a short code, or import one: the same character, marks and level ups included. */

import { useEffect, useRef, useState } from 'react';
import { Check, ClipboardPaste, Copy } from 'lucide-react';
import Dialog from '../Dialog';
import { characterCode, readCharacterCode } from './code';
import type { Character } from '../model';
import './share.css';

export function ShareCodeDialog({ character, onClose }: { character: Character; onClose: () => void }) {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState<boolean | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    characterCode(character).then(setCode);
  }, [character]);

  const copy = () =>
    navigator.clipboard.writeText(code).then(
      () => setCopied(true),
      () => {
        box.current?.select();
        setCopied(false);
      },
    );

  return (
    <Dialog
      title={`Share ${character.name || 'this character'}`}
      onClose={onClose}
      footer={
        <>
          <span className="cb-dialog__progress">{code ? `${code.length} characters` : 'Making the code'}</span>
          <button type="button" className="cb-btn cb-btn--primary" disabled={!code} onClick={copy}>
            {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
            {copied ? 'Copied' : 'Copy code'}
          </button>
        </>
      }
    >
      <p className="cb-pdf__lead">
        Anyone who pastes this code into Import a code on this page gets the same character, with its marks and level ups.
      </p>
      <label className="cb-field cc-code">
        <span className="cb-field__label">Character code</span>
        <textarea ref={box} value={code} readOnly rows={4} onFocus={(e) => e.target.select()} />
      </label>
      {copied === false && <p className="cc-code__note">Copying was blocked: the code is selected, so copy it yourself.</p>}
    </Dialog>
  );
}

export function ImportCodeDialog({ onImport, onClose }: { onImport: (c: Character) => void; onClose: () => void }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ character?: Character; error?: string }>({});
  const latest = useRef('');

  const read = (value: string) => {
    setText(value);
    latest.current = value;
    if (!value.trim()) return setResult({});
    readCharacterCode(value).then(
      (character) => latest.current === value && setResult({ character }),
      (error: Error) => latest.current === value && setResult({ error: error.message }),
    );
  };

  const c = result.character;
  return (
    <Dialog
      title="Import a character"
      onClose={onClose}
      footer={
        <button type="button" className="cb-btn cb-btn--primary" disabled={!c} onClick={() => c && onImport(c)}>
          <ClipboardPaste size={16} aria-hidden="true" />Import character
        </button>
      }
    >
      <p className="cb-pdf__lead">Paste a character code someone shared with you. The character joins your list on this device.</p>
      <label className="cb-field cc-code">
        <span className="cb-field__label">Character code to import</span>
        <textarea value={text} rows={4} placeholder="Paste the code here" onChange={(e) => read(e.target.value)} />
      </label>
      {c && <p className="cc-code__found">{[c.name || 'Unnamed character', c.className, c.subclass].filter(Boolean).join(' · ')}</p>}
      {result.error && <p className="cc-code__note" role="alert">{result.error}</p>}
    </Dialog>
  );
}
